import React, { useState, useEffect, useRef } from 'react';
import { Drawer, Input, Button, Space, Typography, Spin, Tooltip } from 'antd';
import {
  SendOutlined,
  SoundOutlined,
  MutedOutlined,
  CloseOutlined,
  RobotOutlined,
  AudioOutlined,
  LoadingOutlined,
  PlusCircleOutlined,
} from '@ant-design/icons';
import i18n from '../../i18n';
import ChatMessage from './ChatMessage';
import './AIChat.css';

const { TextArea } = Input;
const { Text } = Typography;

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface AIChatProps {
  apiBaseUrl?: string;
}

const AIChat: React.FC<AIChatProps> = ({
  apiBaseUrl = '/api/ai'
}) => {
  const [aiEnabled, setAiEnabled] = useState<boolean | null>(null);
  const [visible, setVisible] = useState(false);
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [language, setLanguage] = useState<string>(() => i18n.language?.slice(0, 2) || localStorage.getItem('language') || 'it');
  const [conversationId, setConversationId] = useState<number | null>(null);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isVoiceSupported, setIsVoiceSupported] = useState(false);
  const [isConversationMode, setIsConversationMode] = useState(false);
  const [conversationState, setConversationState] = useState<'listening' | 'thinking' | 'speaking'>('listening');

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const inputRef = useRef<any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const silenceCheckIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const shouldTranscribeRef = useRef(false);
  const shouldResumeListeningRef = useRef(false);
  const isConversationModeRef = useRef(false);
  const handleVoiceInputRef = useRef<(transcript: string) => void>(() => {});

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Check if AI chat is enabled by platform admin
  useEffect(() => {
    const checkEnabled = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setAiEnabled(false); return; }
        const base = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
        const res = await fetch(`${base}/admin-ai/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setAiEnabled(data.enabled !== false);
        } else {
          setAiEnabled(false);
        }
      } catch {
        setAiEnabled(false);
      }
    };
    checkEnabled();
  }, []);

  // Sync language with app i18n setting
  useEffect(() => {
    const onLangChange = (lng: string) => setLanguage(lng.slice(0, 2));
    i18n.on('languageChanged', onLangChange);
    return () => i18n.off('languageChanged', onLangChange);
  }, []);

  // Ascolta evento dal bottone AI nella bottom navigation mobile
  useEffect(() => {
    const handleOpenChat = () => setVisible(true);
    window.addEventListener('open-ai-chat', handleOpenChat);
    return () => window.removeEventListener('open-ai-chat', handleOpenChat);
  }, []);

  // Check MediaRecorder / microphone support
  useEffect(() => {
    const supported = !!(navigator.mediaDevices && typeof MediaRecorder !== 'undefined');
    setIsVoiceSupported(supported);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-focus input when modal opens
  useEffect(() => {
    if (visible) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [visible]);

  // Load greeting message
  useEffect(() => {
    if (visible && messages.length === 0) {
      const greetings = {
        it: 'Ciao! Sono l\'assistente QuotaPro. Come posso aiutarti oggi?',
        fr: 'Bonjour! Je suis l\'assistant QuotaPro. Comment puis-je vous aider?',
        de: 'Hallo! Ich bin der QuotaPro-Assistent. Wie kann ich Ihnen helfen?',
        en: 'Hello! I\'m the QuotaPro assistant. How can I help you today?'
      };

      setTimeout(() => {
        addMessage('assistant', greetings[language as keyof typeof greetings] || greetings.it);
      }, 300);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  const addMessage = (role: 'user' | 'assistant', content: string) => {
    const newMessage: Message = {
      id: `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      role,
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, newMessage]);
  };

  const sendMessage = async () => {
    if (!inputValue.trim() || loading) return;

    const userMessage = inputValue.trim();
    setInputValue('');
    addMessage('user', userMessage);
    setLoading(true);
    setIsTyping(true);

    // Create placeholder for streaming response
    const assistantMessageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          language
        })
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      // Read SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            if (data.chunk) {
              // Append chunk to response
              fullResponse += data.chunk;
              const currentResponse = fullResponse;
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: currentResponse }
                  : msg
              ));
            }

            if (data.done && data.conversation_id && !conversationId) {
              setConversationId(data.conversation_id);
            }
          }
        }
      }

      // Auto TTS if enabled
      if (ttsEnabled && fullResponse) {
        playTTS(fullResponse);
      }

    } catch (error) {
      console.error('Chat error:', error);
      // Remove placeholder and add error message
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      addMessage('assistant', 'Mi dispiace, si è verificato un errore. Riprova.');
    } finally {
      setLoading(false);
      setIsTyping(false);
    }
  };

  const playTTS = async (text: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/tts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          text,
          language
        })
      });

      if (!response.ok) {
        throw new Error('TTS request failed');
      }

      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);

      if (audioRef.current) {
        audioRef.current.pause();
      }

      const audio = new Audio(audioUrl);
      audioRef.current = audio;

      audio.play();

      // Cleanup URL after playback
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
      };

    } catch (error) {
      console.error('TTS error:', error);
    }
  };

  const recordAndTranscribe = async () => {
    if (!navigator.mediaDevices?.getUserMedia) return;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });

      // Silence detection via Web Audio API — time domain (amplitude), more accurate than frequency
      const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
      const audioContext = new AudioContextClass();
      const source = audioContext.createMediaStreamSource(stream);
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      source.connect(analyser);
      const dataArray = new Uint8Array(analyser.fftSize); // time domain buffer

      // Time domain RMS: 128 = silence (center), deviation from 128 = sound
      // Normalized threshold 0.0–1.0: 0.02 = very quiet, 0.05 = normal silence threshold
      const SILENCE_THRESHOLD = 0.02;
      const SILENCE_DURATION_MS = 2000; // ms of silence before stopping (longer = catches full sentences)
      const MIN_RECORDING_MS = 800;     // minimum recording time before allowing auto-stop
      const recordingStart = Date.now();
      let silenceStart: number | null = null;

      // Pick best supported MIME type for Whisper compatibility
      const mimeType = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg']
        .find(t => MediaRecorder.isTypeSupported(t)) || '';

      const mediaRecorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = mediaRecorder;
      const chunks: BlobPart[] = [];
      shouldTranscribeRef.current = true; // by default we want to transcribe when stopped

      const silenceInterval = setInterval(() => {
        analyser.getByteTimeDomainData(dataArray);
        // Compute normalized RMS: each sample is 0–255, center (silence) = 128
        const rms = Math.sqrt(
          dataArray.reduce((s, v) => { const n = (v - 128) / 128; return s + n * n; }, 0) / dataArray.length
        );
        const elapsed = Date.now() - recordingStart;

        if (rms < SILENCE_THRESHOLD) {
          if (!silenceStart) silenceStart = Date.now();
          else if (elapsed > MIN_RECORDING_MS && Date.now() - silenceStart > SILENCE_DURATION_MS) {
            clearInterval(silenceInterval);
            silenceCheckIntervalRef.current = null;
            if (mediaRecorder.state === 'recording') {
              mediaRecorder.stop();
            }
          }
        } else {
          silenceStart = null;
        }
      }, 100);
      silenceCheckIntervalRef.current = silenceInterval;

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunks.push(e.data);
      };

      mediaRecorder.onstop = async () => {
        // Cleanup audio resources
        if (silenceCheckIntervalRef.current) {
          clearInterval(silenceCheckIntervalRef.current);
          silenceCheckIntervalRef.current = null;
        }
        source.disconnect();
        try { audioContext.close(); } catch (_) {}
        stream.getTracks().forEach(t => t.stop());
        setIsRecording(false);

        // If stopped externally (user pressed stop / typed), skip transcription
        if (!shouldTranscribeRef.current) return;

        const blob = new Blob(chunks, { type: mimeType || 'audio/webm' });

        // Too small → probably silence with no speech
        if (blob.size < 2000) {
          if (isConversationModeRef.current && shouldResumeListeningRef.current) {
            setTimeout(recordAndTranscribe, 400);
          }
          return;
        }

        // Send to Whisper via backend
        try {
          const formData = new FormData();
          formData.append('audio', blob, 'audio.webm');
          formData.append('language', language);

          const token = localStorage.getItem('token');
          const res = await fetch(`${apiBaseUrl}/transcribe`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          });

          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const { text } = await res.json();

          if (text?.trim()) {
            handleVoiceInputRef.current(text.trim());
          } else if (isConversationModeRef.current && shouldResumeListeningRef.current) {
            // Empty transcript (noise only) → restart listening
            setTimeout(recordAndTranscribe, 400);
          }
        } catch (err) {
          console.error('[Whisper] Transcription error:', err);
          if (isConversationModeRef.current && shouldResumeListeningRef.current) {
            setTimeout(recordAndTranscribe, 600);
          }
        }
      };

      mediaRecorder.start(200); // collect chunks every 200ms
      setIsRecording(true);

    } catch (err: any) {
      console.error('[Mic] Access error:', err);
      setIsRecording(false);
      isConversationModeRef.current = false;
      setIsConversationMode(false);
      shouldResumeListeningRef.current = false;
    }
  };

  const handleVoiceInput = (transcript: string) => {
    if (isConversationModeRef.current) {
      // In conversation mode: auto-send immediately
      setConversationState('thinking');
      setInputValue(transcript);
      // Disable auto-restart while we process + play TTS response
      shouldResumeListeningRef.current = false;
      setTimeout(() => {
        sendMessageInConversation(transcript);
      }, 100);
    } else {
      // Normal mode: just fill the textarea
      setInputValue(transcript);
      setTimeout(() => {
        sendMessage();
      }, 300);
    }
  };

  // Keep ref pointing to latest version so recognition callbacks (set up in useEffect) never use stale closures
  handleVoiceInputRef.current = handleVoiceInput;

  const sendMessageInConversation = async (text: string) => {
    if (!text.trim() || loading) return;

    const userMessage = text.trim();
    addMessage('user', userMessage);
    setLoading(true);

    // Create placeholder for streaming response
    const assistantMessageId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const assistantMessage: Message = {
      id: assistantMessageId,
      role: 'assistant',
      content: '',
      timestamp: new Date()
    };
    setMessages(prev => [...prev, assistantMessage]);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${apiBaseUrl}/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          message: userMessage,
          conversation_id: conversationId,
          language
        })
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();
      let fullResponse = '';

      if (!reader) {
        throw new Error('No response body');
      }

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = JSON.parse(line.substring(6));

            if (data.chunk) {
              fullResponse += data.chunk;
              const currentResponse = fullResponse;
              setMessages(prev => prev.map(msg =>
                msg.id === assistantMessageId
                  ? { ...msg, content: currentResponse }
                  : msg
              ));
            }

            if (data.done && data.conversation_id && !conversationId) {
              setConversationId(data.conversation_id);
            }
          }
        }
      }

      // In conversation mode: play TTS and resume listening
      if (isConversationModeRef.current && fullResponse) {
        setConversationState('speaking');
        shouldResumeListeningRef.current = true;
        await playTTSInConversation(fullResponse);
      }

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => prev.filter(msg => msg.id !== assistantMessageId));
      addMessage('assistant', 'Mi dispiace, si è verificato un errore. Riprova.');

      // Exit conversation mode on error
      setIsConversationMode(false);
      setIsRecording(false);
    } finally {
      setLoading(false);
      setInputValue('');
    }
  };

  const playTTSInConversation = async (text: string): Promise<void> => {
    return new Promise(async (resolve, reject) => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${apiBaseUrl}/tts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            text,
            language
          })
        });

        if (!response.ok) {
          throw new Error('TTS request failed');
        }

        const audioBlob = await response.blob();
        const audioUrl = URL.createObjectURL(audioBlob);

        if (audioRef.current) {
          audioRef.current.pause();
        }

        const audio = new Audio(audioUrl);
        audioRef.current = audio;

        // Resume listening after speech ends naturally
        audio.onended = () => {
          URL.revokeObjectURL(audioUrl);

          if (shouldResumeListeningRef.current && isConversationModeRef.current) {
            setTimeout(() => {
              setConversationState('listening');
              resumeListening();
            }, 500);
          }
          resolve();
        };

        // Audio paused externally (user stopped conversation mode mid-speech)
        // Resolve immediately so the await in sendMessageInConversation unblocks
        // and the finally block can run setLoading(false)
        audio.onpause = () => {
          URL.revokeObjectURL(audioUrl);
          resolve();
        };

        audio.onerror = (error) => {
          URL.revokeObjectURL(audioUrl);
          console.error('Audio playback error:', error);
          reject(new Error('Audio playback failed'));
        };

        audio.play().catch(reject);

      } catch (error) {
        console.error('TTS error:', error);
        reject(error);
      }
    });
  };

  const resumeListening = () => {
    if (isConversationModeRef.current) {
      recordAndTranscribe();
    }
  };

  const startConversationMode = () => {
    setIsConversationMode(true);
    isConversationModeRef.current = true;
    setConversationState('listening');
    shouldResumeListeningRef.current = true;
    recordAndTranscribe();
  };

  const stopConversationMode = () => {
    setIsConversationMode(false);
    isConversationModeRef.current = false;
    setIsRecording(false);
    shouldResumeListeningRef.current = false;
    shouldTranscribeRef.current = false; // discard any in-progress recording

    // Stop silence detection
    if (silenceCheckIntervalRef.current) {
      clearInterval(silenceCheckIntervalRef.current);
      silenceCheckIntervalRef.current = null;
    }

    // Stop MediaRecorder (onstop will see shouldTranscribeRef=false and skip)
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      try { mediaRecorderRef.current.stop(); } catch (_) {}
    }

    // Stop audio playback — triggers audio.onpause which resolves the playTTSInConversation
    // promise, unblocking the await and allowing the finally block to run setLoading(false)
    if (audioRef.current) {
      audioRef.current.pause();
    }

    // Safety net: reset loading state immediately in case the promise doesn't resolve fast enough
    setLoading(false);
    setIsTyping(false);
  };

  const handleNewChat = () => {
    // Stop voice mode if active
    if (isConversationMode) {
      stopConversationMode();
    }
    // Stop any playing audio
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Reset state
    setMessages([]);
    setConversationId(null);
    setInputValue('');
    setLoading(false);
    setIsTyping(false);

    // Show greeting again
    const greetings = {
      it: 'Ciao! Sono l\'assistente QuotaPro. Come posso aiutarti oggi?',
      fr: 'Bonjour! Je suis l\'assistant QuotaPro. Comment puis-je vous aider?',
      de: 'Hallo! Ich bin der QuotaPro-Assistent. Wie kann ich Ihnen helfen?',
      en: 'Hello! I\'m the QuotaPro assistant. How can I help you today?'
    };
    setTimeout(() => {
      addMessage('assistant', greetings[language as keyof typeof greetings] || greetings.it);
    }, 150);

    // Refocus input
    setTimeout(() => inputRef.current?.focus(), 300);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  if (!aiEnabled) return null;

  return (
    <>
      {/* Desktop: linguetta verticale incollata al bordo destro */}
      {!isMobile && (
        <div
          className={`ai-chat-tab ${visible ? 'ai-chat-tab--open' : ''}`}
          onClick={() => setVisible(v => !v)}
          title="AI Assistant"
        >
          <RobotOutlined style={{ fontSize: 18 }} />
          <span className="ai-chat-tab-label">AI</span>
        </div>
      )}

      {/* Drawer: dal basso su mobile, da destra su desktop */}
      <Drawer
        open={visible}
        onClose={() => setVisible(false)}
        placement={isMobile ? 'bottom' : 'right'}
        width={isMobile ? undefined : 520}
        height={isMobile ? '78vh' : undefined}
        closable={false}
        className="ai-chat-drawer"
        styles={{
          body: {
            padding: 0,
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            overflow: 'hidden',
          },
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(1, 13, 105, 0.12)',
          }
        }}
      >
        {/* Premium Header */}
        <div className="ai-chat-header">
          <div className="ai-chat-header-content">
            <div className="ai-chat-header-left">
              <div className="ai-chat-avatar">
                <RobotOutlined />
              </div>
              <div className="ai-chat-header-title">
                <Text strong style={{ color: '#fff', fontSize: 16, display: 'block' }}>
                  QuotaPro AI Assistant
                </Text>
                <Text style={{ color: 'rgba(255,255,255,0.8)', fontSize: 12 }}>
                  {isTyping ? 'Sta scrivendo...' : 'Online'}
                </Text>
              </div>
            </div>
            <Space size="small">
              <Tooltip title="Nuova conversazione" placement="bottom">
                <Button
                  icon={<PlusCircleOutlined />}
                  size="middle"
                  onClick={handleNewChat}
                  className="ai-chat-header-btn"
                  style={{
                    background: 'rgba(255,255,255,0.1)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                  }}
                />
              </Tooltip>
              <Tooltip title={ttsEnabled ? 'Disattiva lettura vocale' : 'Attiva lettura vocale delle risposte'} placement="bottom">
                <Button
                  icon={ttsEnabled ? <SoundOutlined /> : <MutedOutlined />}
                  size="middle"
                  onClick={() => setTtsEnabled(!ttsEnabled)}
                  className="ai-chat-header-btn"
                  style={{
                    background: ttsEnabled ? 'rgba(235,42,221,0.35)' : 'rgba(255,255,255,0.1)',
                    border: ttsEnabled ? '1px solid rgba(235,42,221,0.6)' : '1px solid rgba(255,255,255,0.2)',
                    color: '#fff',
                  }}
                />
              </Tooltip>
              <Button
                icon={<CloseOutlined />}
                size="middle"
                onClick={() => setVisible(false)}
                className="ai-chat-header-btn"
                style={{
                  background: 'rgba(255,255,255,0.1)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                }}
              />
            </Space>
          </div>
        </div>

        {/* Messages Container */}
        <div className="ai-chat-messages-container">
          <div className="ai-chat-messages">
            {messages.map(msg => (
              <ChatMessage
                key={msg.id}
                message={msg}
                onPlayTTS={ttsEnabled ? () => playTTS(msg.content) : undefined}
              />
            ))}
            {loading && (
              <div className="ai-chat-typing-indicator">
                <Spin size="small" />
                <span style={{ marginLeft: 8, color: '#666' }}>L'assistente sta pensando...</span>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        {/* Compact Voice Status Bar */}
        {isConversationMode && (
          <div className="ai-chat-voice-bar">
            <div className="ai-chat-voice-bar-left">
              {conversationState === 'listening' && (
                <>
                  <span className="ai-chat-voice-bar-dot ai-chat-voice-bar-dot--pulse" />
                  <span className="ai-chat-voice-bar-label">Sto ascoltando... parla ora</span>
                </>
              )}
              {conversationState === 'thinking' && (
                <>
                  <LoadingOutlined style={{ color: '#EB2ADD', fontSize: 14 }} />
                  <span className="ai-chat-voice-bar-label">Elaborazione in corso...</span>
                </>
              )}
              {conversationState === 'speaking' && (
                <>
                  <SoundOutlined style={{ color: '#010D69', fontSize: 14 }} />
                  <span className="ai-chat-voice-bar-label">L&apos;assistente sta parlando...</span>
                </>
              )}
            </div>
            <Button
              size="small"
              danger
              onClick={stopConversationMode}
              style={{ borderRadius: 6, fontWeight: 600, fontSize: 12 }}
            >
              Stop voce
            </Button>
          </div>
        )}

        {/* Premium Input Container */}
        <div className="ai-chat-input-container">
          <div className="ai-chat-input-wrapper">
            <TextArea
              ref={inputRef}
              value={inputValue}
              onChange={e => {
                setInputValue(e.target.value);
                // Typing while in voice mode exits voice mode
                if (isConversationMode && e.target.value.length > 0) {
                  stopConversationMode();
                }
              }}
              onKeyPress={handleKeyPress}
              placeholder={
                isConversationMode
                  ? 'Scrivi per interrompere la conversazione vocale...'
                  : isRecording
                  ? 'Ascoltando...'
                  : "Scrivi o parla con l'assistente..."
              }
              autoSize={{ minRows: 1, maxRows: 4 }}
              disabled={loading}
              className="ai-chat-textarea"
            />
            <div className="ai-chat-input-actions">
              {!inputValue.trim() && isVoiceSupported ? (
                // Microphone button: toggles voice mode on/off
                <Button
                  type="primary"
                  icon={<AudioOutlined />}
                  onClick={isConversationMode ? stopConversationMode : startConversationMode}
                  size="large"
                  className={`ai-chat-voice-btn${isConversationMode ? ' ai-chat-voice-btn--active' : ''}`}
                />
              ) : (
                // Send button: sends text, exits voice mode if active
                <Button
                  type="primary"
                  icon={<SendOutlined />}
                  onClick={() => {
                    if (isConversationMode) stopConversationMode();
                    sendMessage();
                  }}
                  loading={loading}
                  disabled={!inputValue.trim()}
                  size="large"
                  className="ai-chat-send-btn"
                >
                  {isMobile ? '' : 'Invia'}
                </Button>
              )}
            </div>
          </div>
        </div>
      </Drawer>
    </>
  );
};

export default AIChat;
